import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { AuthMeResponse } from '@brightlocal/api-client';

import { useGTM, AUTH_ME_QUERY_KEY } from 'shared/hooks';
import { formatErrorDescription, type ErrorSource } from 'shared/utils';

interface AuthFormTrackingConfig {
  /** GTM event prefix, e.g. 'login' produces 'login_started' */
  eventPrefix: string;
  /** eventCategory included in every event payload */
  eventCategory: string;
  /** eventLabel identifying the auth method (e.g. 'email') */
  eventLabel: string;
  /** eventAction included in every event payload (defaults to null) */
  eventAction?: string | null;
}

const getBase = ({
  eventCategory,
  eventAction = null,
  eventLabel,
  eventDescription = null
}: {
  eventCategory: string;
  eventLabel: string;
  eventAction?: string | null;
  eventDescription?: string | null;
}) => ({
  virtualURL: globalThis.window?.location.href ?? '',
  eventCategory,
  eventAction,
  eventLabel,
  eventDescription
});

/**
 * Configurable GTM tracking for email/password auth flows (login & sign-up).
 * Provides trackStarted, trackCompleted, and trackError with consistent event structure.
 */
export const useAuthFormTracking = ({
  eventPrefix,
  eventCategory,
  eventLabel,
  eventAction = null
}: AuthFormTrackingConfig) => {
  const { pushEvent, buildAuthData } = useGTM();
  const queryClient = useQueryClient();

  const trackStarted = useCallback(() => {
    pushEvent(`${eventPrefix}_started`, {
      ...getBase({ eventCategory, eventAction, eventLabel }),
      ...buildAuthData()
    });
  }, [pushEvent, buildAuthData, eventPrefix, eventCategory, eventAction, eventLabel]);

  const trackCompleted = useCallback(() => {
    const authData = queryClient.getQueryData<AuthMeResponse>(AUTH_ME_QUERY_KEY);

    pushEvent(`${eventPrefix}_completed`, {
      ...getBase({ eventCategory, eventAction, eventLabel }),
      ...buildAuthData(authData)
    });
  }, [pushEvent, buildAuthData, queryClient, eventPrefix, eventCategory, eventAction, eventLabel]);

  const trackError = useCallback(
    (message: string, errorSource: ErrorSource) => {
      pushEvent(`${eventPrefix}_error`, {
        ...getBase({
          eventCategory,
          eventAction,
          eventLabel,
          eventDescription: formatErrorDescription(errorSource, message)
        }),
        ...buildAuthData()
      });
    },
    [pushEvent, buildAuthData, eventPrefix, eventCategory, eventAction, eventLabel]
  );

  return { trackStarted, trackCompleted, trackError };
};
